import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useT } from "../../i18n/LanguageProvider";
import { errorText } from "../../i18n/serverErrors";
import { pairPrinter, printerTail, type ResolvedPrinter } from "../../platform/printerRegistry";
import { printSaleReceipt, receiptStepAvailable, saleReceiptPrinter } from "./printSaleReceipt";
import { afterReceipt, firstStep, type SaleCompleteStep } from "./saleCompleteSteps";
import { SaleConfirmationOverlay } from "./SaleConfirmationOverlay";
import { ShopFloorIcon } from "./ShopFloorIcon";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-156 — what happens after a kiosk sale (OD-34 as refined 2026-09-11)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   sale → "Print a receipt?" → earnings, only when commission was earned,
 *   with the chime → (the screen's own below-floor prompt, after `onDone`)
 *
 * ONE COMPONENT FOR EVERY SURFACE that logs a floor sale — the till, the
 * bouquet builder's Sell and the counter handover of an order — mounted where
 * `SaleConfirmationOverlay` used to be mounted on its own, so the three cannot
 * drift (the 2026-08-09 lesson: a copied inline version shipped silent).
 *
 * THE QUESTION IS THE SALE-COMPLETE MOMENT. It closes only on a tap: Yes
 * prints with a status line and a retry, No skips. OD-18's 2.5-second
 * auto-dismiss is NOT repealed — it now governs the earnings card, which is
 * the one panel that still appears by itself, and the chime moved with it:
 * a sale that earned nothing ends silently once the question is answered.
 *
 * The receipt comes BEFORE the earnings because the customer is still at the
 * counter for the first and must not be there for the second.
 *
 * In a plain browser on the live site the question is ABSENT, not disabled
 * (`receiptStepAvailable`): the website changes nothing. The development
 * server shows it so it can be looked at.
 *
 * The owner's pushback record: the agent proposed a "Print receipt" toggle
 * beside Cash/Card so nothing blocks after the sale; the owner wants the
 * explicit question after every sale, on all sale kinds. Do not re-raise.
 */

export function SaleCompleteFlow({
  saleId,
  commissionPercent,
  commissionEarnedEur,
  summary,
  onDone,
}: {
  saleId: string;
  commissionPercent: string | null;
  /** "0.00" for a handover: the order's money is its own; no earnings card. */
  commissionEarnedEur: string;
  /** One line under the question — the total, how it was paid. Optional. */
  summary?: string;
  onDone: () => void;
}) {
  const { t } = useT();
  const [step, setStep] = useState<SaleCompleteStep>(() =>
    firstStep(receiptStepAvailable(import.meta.env.DEV), commissionEarnedEur),
  );
  // Resolved per sale, and again after pairing from the card itself.
  const [printer, setPrinter] = useState<ResolvedPrinter | null>(() => saleReceiptPrinter());
  const [pairing, setPairing] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  useEffect(() => {
    if (step === "done") doneRef.current();
  }, [step]);

  /**
   * A print outlives the question if the screen changes under it —
   * `printRaster` takes seconds — so its result must not land in a component
   * that is gone. The printer is not orphaned: `printRaster` disconnects in
   * its own `finally`.
   */
  const onScreen = useRef(true);
  useEffect(() => {
    onScreen.current = true;
    return () => {
      onScreen.current = false;
    };
  }, []);

  const print = async () => {
    if (!printer) return;
    setStep("printing");
    setFailure(null);
    const result = await printSaleReceipt(printer, saleId);
    if (!onScreen.current) return;
    if (result.outcome === "printed") {
      setStep(afterReceipt(commissionEarnedEur));
      return;
    }
    setFailure(
      errorText(
        result.cause,
        result.outcome === "could-not-print" ? "receipt.couldNotPrint" : "receipt.couldNotLoad",
      ),
    );
    setStep("failed");
  };

  const pair = async () => {
    setPairing(true);
    setFailure(null);
    try {
      await pairPrinter("receipts");
    } catch (err: unknown) {
      setFailure(errorText(err, "printer.couldNotPair"));
      return;
    } finally {
      setPairing(false);
    }
    setPrinter(saleReceiptPrinter());
  };

  const skip = () => setStep(afterReceipt(commissionEarnedEur));

  if (step === "earnings") {
    return (
      <SaleConfirmationOverlay
        commissionPercent={commissionPercent}
        commissionEarnedEur={commissionEarnedEur}
        onDismiss={() => setStep("done")}
      />
    );
  }
  if (step === "done") return null;

  const printerName = printer
    ? `${printer.printer.name ?? t("note.thePrinter")} …${printerTail(printer.printer.deviceId)}`
    : null;

  return createPortal(
    <div
      className="sf-receipt-layer"
      role="dialog"
      aria-modal="true"
      aria-label={t("receipt.question")}
    >
      <div className={step === "failed" ? "sf-receipt-card is-failed" : "sf-receipt-card"}>
        <span className="sf-receipt-icon" aria-hidden="true">
          <ShopFloorIcon name="print" size={28} />
        </span>

        {step === "question" ? (
          <>
            <p className="sf-receipt-title">{t("receipt.question")}</p>
            {printer ? (
              summary ? <p className="sf-receipt-sub">{summary}</p> : null
            ) : (
              <p className="sf-receipt-sub">
                <strong>{t("printer.nonePaired")}</strong> {t("printer.pairToPrint")}
              </p>
            )}
            {failure ? <div className="alert error sf-receipt-error">{failure}</div> : null}
            <div className="sf-receipt-actions">
              {printer ? (
                <button type="button" className="button" onClick={() => void print()}>
                  {t("receipt.yes")}
                </button>
              ) : (
                <button
                  type="button"
                  className="button"
                  onClick={() => void pair()}
                  disabled={pairing}
                >
                  {pairing ? t("printer.waiting") : t("printer.pairAPrinter")}
                </button>
              )}
              <button type="button" className="button tinted" onClick={skip} disabled={pairing}>
                {t("receipt.no")}
              </button>
            </div>
            {printerName ? (
              <p className="sf-receipt-note">{t("receipt.printingTo", { printer: printerName })}</p>
            ) : null}
          </>
        ) : step === "printing" ? (
          <>
            <p className="sf-receipt-title">{t("receipt.printing")}</p>
            {printerName ? (
              <p className="sf-receipt-sub">{t("receipt.reaching", { printer: printerName })}</p>
            ) : null}
            <div className="sf-receipt-progress" aria-hidden="true">
              <span />
            </div>
          </>
        ) : (
          <>
            <p className="sf-receipt-title">{t("receipt.didNotPrint")}</p>
            {failure ? <p className="sf-receipt-sub">{failure}</p> : null}
            <div className="sf-receipt-actions">
              <button type="button" className="button" onClick={() => void print()}>
                {t("receipt.tryAgain")}
              </button>
              <button type="button" className="button tinted" onClick={skip}>
                {t("receipt.skip")}
              </button>
            </div>
            <p className="sf-receipt-note">{t("receipt.savedAnyway")}</p>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
